import { RouteError } from '@/components/route-error'
import { RoutePending } from '@/components/route-pending'
import * as React from 'react'
import { createFileRoute,Outlet,useNavigate,useSearch } from '@tanstack/react-router'
import { z } from 'zod'
import { useQuery,useQueryClient,useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
Search,
Plus,
X,
RefreshCw,
MoreHorizontal,
Trash2,
Loader2,
} from 'lucide-react'
import { PageBreadcrumb } from '@/components/dashboard/page-breadcrumb'
import { DataTablePagination } from '@/components/dashboard/data-table-pagination'
import {
Table,
TableBody,
TableCell,
TableHead,
TableHeader,
TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import {
Dialog,
DialogContent,
DialogDescription,
DialogFooter,
DialogHeader,
DialogTitle,
} from '@/components/ui/dialog'
import {
DropdownMenu,
DropdownMenuContent,
DropdownMenuItem,
DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
Select,
SelectContent,
SelectItem,
SelectTrigger,
SelectValue,
} from '@/components/ui/select'
import {
Tooltip,
TooltipContent,
TooltipProvider,
TooltipTrigger,
} from '@/components/ui/tooltip'
import { Separator } from '@/components/ui/separator'
import { Checkbox } from '@/components/ui/checkbox'
import { ImageWithSkeleton } from '@/components/ui/image-with-skeleton'
import { queryKeys } from '@/lib/query-keys'
import { getErrorMessage,useSelectedShopId } from '@/features/shared'
import { customersApi } from '@/features/customers/api'
import { useCustomerArchive } from '@/features/customers/hooks/use-customer-archive'
import { RestoreCustomerButton } from '@/features/customers/components/restore-customer-button'
import { ArchivedMatchHint } from '@/features/customers/components/archived-match-hint'
import { ArchiveCustomerDialogs } from '@/features/customers/components/archive-customer-dialogs'
import { ArchivedCustomerPanel } from '@/features/customers/archived-customer-panel'
import { queueSessionsApi } from '@/features/pos/queue-sessions/api'
import { isAxiosError } from '@/lib/api-client'
import { CustomerTypeBadge } from '@/features/customers/components/customer-type-badge'
import { CustomerStatusBadge,CustomerStatusDot } from '@/features/customers/components/customer-status-badge'
import { useCan,useMe } from '@/features/access/use-permissions'
import type { Customer,CustomerGroup } from '@/types'
import { CustomerActionsMenu, type CustomerActions } from '@/features/customers/components/customer-actions-menu'

const CustomerSidePanel = React.lazy(() =>
  import('@/features/customers/customer-side-panel').then((m) => ({
    default: m.CustomerSidePanel,
  }))
)

const EraseCustomerDialog = React.lazy(() => import('@/features/customers/erase-customer-dialog').then((m) => ({ default: m.EraseCustomerDialog })))
const MergeCustomerDialog = React.lazy(() => import('@/features/customers/merge-customer-dialog').then((m) => ({ default: m.MergeCustomerDialog })))
const AddCustomerSheet = React.lazy(() =>
  import('@/features/customers/add-customer-sheet').then((m) => ({
    default: m.AddCustomerSheet,
  }))
)

const searchSchema = z.object({
  page: z.number().catch(1),
  limit: z.number().catch(20),
  search: z.string().catch(''),
  customerType: z.string().catch(''),
  status: z.string().catch(''),
})

export const Route = createFileRoute('/_dashboard/customers')({
  pendingComponent: RoutePending,
  errorComponent: RouteError,
  component: CustomersLayout,
  validateSearch: searchSchema,
})

function CustomersLayout() {
  return <Outlet />
}

// Auto-open suppression (same pattern as the products/sales pages): once the
// user closes the panel, don't auto-reopen it for the same page/search/filter
// combination — only a new page signature re-enables the auto-select.
let suppressedAutoOpenSignature: string | null = null

function AvatarCircle({ firstName, lastName, avatarUrl }: { firstName: string; lastName: string; avatarUrl?: string }) {
  const initials = ((firstName?.[0] ?? '') + (lastName?.[0] ?? '')).toUpperCase() || '?'

  return (
    <div className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full border bg-muted text-xs font-medium text-muted-foreground">
      {avatarUrl ? (
        <ImageWithSkeleton src={avatarUrl} alt={`${firstName} ${lastName}`} className="h-full w-full object-cover" />
      ) : (
        <span>{initials}</span>
      )}
    </div>
  )
}

export function CustomersPage({ selectedId }: { selectedId: string | undefined }) {
  const navigate = useNavigate()
  const shopId = useSelectedShopId() ?? ''
  const queryClient = useQueryClient()
  const canManageCustomers = useCan('MANAGE_CUSTOMERS')
  const { page, limit, search: searchQuery, customerType, status } = useSearch({ from: Route.id })

  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(new Set())
  const [searchInput, setSearchInput] = React.useState(searchQuery)
  const [isRefreshing, setIsRefreshing] = React.useState(false)
  const [isAddSheetOpen, setIsAddSheetOpen] = React.useState(false)
  const [editingCustomer, setEditingCustomer] = React.useState<Customer | null>(null)
  const [deletingCustomer, setDeletingCustomer] = React.useState<Customer | null>(null)
  const [mergingCustomer, setMergingCustomer] = React.useState<Customer | null>(null)
  const [erasingCustomer, setErasingCustomer] = React.useState<Customer | null>(null)
  const isOwner = useMe().data?.role === 'OWNER'
  const [flaggingCustomer, setFlaggingCustomer] = React.useState<Customer | null>(null)
  const [banningCustomer, setBanningCustomer] = React.useState<Customer | null>(null)
  const [actionReason, setActionReason] = React.useState('')

  const groupsQuery = useQuery({
    queryKey: queryKeys.customerGroups(),
    queryFn: () => customersApi.groups.list({ isActive: true }),
    staleTime: 60_000,
  })

  const allGroups: CustomerGroup[] = React.useMemo(() => {
    const raw = groupsQuery.data
    if (!raw) return []
    return Array.isArray(raw) ? raw : raw.data
  }, [groupsQuery.data])

  const groupsLoading = groupsQuery.isLoading

  function handleRowSelect(id: string) {
    const newIds = new Set(selectedIds)
    if (newIds.has(id)) {
      newIds.delete(id)
    } else {
      newIds.add(id)
    }
    setSelectedIds(newIds)
  }

  const bulkAddingToGroup = useMutation({
    mutationFn: (groupId: string) => 
      customersApi.customers.addToGroupBulk(Array.from(selectedIds), groupId),
    onSuccess: () => {
      toast.success(`Added ${selectedIds.size} customers to group`)
      setSelectedIds(new Set())
      void queryClient.invalidateQueries({ queryKey: queryKeys.customers() })
    },
    onError: (error) => toast.error(getErrorMessage(error, 'Failed to add customers to group')),
  })

  const setSelectedId = React.useCallback(
    (id: string | null) => {
      if (id) {
        navigate({
          to: '/customers/$item',
          params: { item: id },
          search: (prev) => ({ ...prev }),
        })
        return
      }
      navigate({ to: '/customers', search: (prev) => ({ ...prev }) })
    },
    [navigate],
  )

  const startSession = useMutation({
    mutationFn: (customer: Customer) => queueSessionsApi.create({
      shopId,
      customerId: customer.id,
      customerTypeAtCheckIn: customer.customerType,
    }),
    onSuccess: (_data, customer) => {
      toast.success(`${customer.firstName} added to queue`)
      void queryClient.invalidateQueries({ queryKey: queryKeys.queueSessions.view(shopId) })
    },
    onError: (error) => {
      const data = isAxiosError(error) ? error.response?.data : undefined
      const message =
        typeof data === 'string' ? data
        : typeof data?.message === 'string' ? data.message
        : 'Failed to start session'
      toast.error(message)
    },
  })

  const [bulkDeletingIds, setBulkDeletingIds] = React.useState<string[] | null>(null)

  // "Delete" is an archive: restorable, and it closes the online login.
  const { archive: deleteCustomer, bulkArchive: bulkDeleteCustomers, restore: restoreCustomer } = useCustomerArchive({
    onArchived: () => { setSelectedId(null); setDeletingCustomer(null) },
    onBulkArchived: () => { setSelectedIds(new Set()); setBulkDeletingIds(null) },
    // Restored = active again: leave the Archived view (where they'd now be missing) and open them.
    onRestored: (c) => { void navigate({ to: '/customers/$item', params: { item: c.id }, search: (prev) => ({ ...prev, status: '', page: 1 }) }) },
  })

  const flagCustomer = useMutation({
    mutationFn: ({ id, flagReason }: { id: string; flagReason: string }) =>
      customersApi.customers.flag(id, flagReason),
    onMutate: async ({ id, flagReason }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.customer(id) })
      await queryClient.cancelQueries({ queryKey: queryKeys.customers() })
      
      const prevCustomer = queryClient.getQueryData<Customer>(queryKeys.customer(id))
      
      queryClient.setQueryData<Customer>(queryKeys.customer(id), (old) => {
        if (!old) return old
        return { ...old, status: 'FLAGGED', flagReason }
      })
      
      return { prevCustomer }
    },
    onSuccess: () => {
      toast.success('Customer flagged')
      setFlaggingCustomer(null)
      setActionReason('')
    },
    onError: (_err, { id }, context) => {
      if (context?.prevCustomer) {
        queryClient.setQueryData(queryKeys.customer(id), context.prevCustomer)
      }
      toast.error('Failed to flag customer')
    },
    onSettled: (_data, _err, { id }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.customer(id) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.customers() })
    },
  })

  const unflagCustomer = useMutation({
    mutationFn: (id: string) => customersApi.customers.unflag(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.customer(id) })
      await queryClient.cancelQueries({ queryKey: queryKeys.customers() })
      
      const prevCustomer = queryClient.getQueryData<Customer>(queryKeys.customer(id))
      
      queryClient.setQueryData<Customer>(queryKeys.customer(id), (old) => {
        if (!old) return old
        return { ...old, status: 'ACTIVE', flagReason: undefined }
      })
      
      return { prevCustomer }
    },
    onSuccess: () => {
      toast.success('Customer unflagged')
    },
    onError: (_err, id, context) => {
      if (context?.prevCustomer) {
        queryClient.setQueryData(queryKeys.customer(id), context.prevCustomer)
      }
      toast.error('Failed to unflag customer')
    },
    onSettled: (_data, _err, id) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.customer(id) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.customers() })
    },
  })

  const banCustomer = useMutation({
    mutationFn: ({ id, bannedReason }: { id: string; bannedReason: string }) =>
      customersApi.customers.ban(id, bannedReason),
    onMutate: async ({ id, bannedReason }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.customer(id) })
      await queryClient.cancelQueries({ queryKey: queryKeys.customers() })
      
      const prevCustomer = queryClient.getQueryData<Customer>(queryKeys.customer(id))
      
      queryClient.setQueryData<Customer>(queryKeys.customer(id), (old) => {
        if (!old) return old
        return { ...old, status: 'BANNED', bannedReason }
      })
      
      return { prevCustomer }
    },
    onSuccess: () => {
      toast.success('Customer banned')
      setBanningCustomer(null)
      setActionReason('')
    },
    onError: (_err, { id }, context) => {
      if (context?.prevCustomer) {
        queryClient.setQueryData(queryKeys.customer(id), context.prevCustomer)
      }
      toast.error('Failed to ban customer')
    },
    onSettled: (_data, _err, { id }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.customer(id) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.customers() })
    },
  })

  const unbanCustomer = useMutation({
    mutationFn: (id: string) => customersApi.customers.unban(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.customer(id) })
      await queryClient.cancelQueries({ queryKey: queryKeys.customers() })
      
      const prevCustomer = queryClient.getQueryData<Customer>(queryKeys.customer(id))
      
      queryClient.setQueryData<Customer>(queryKeys.customer(id), (old) => {
        if (!old) return old
        return { ...old, status: 'ACTIVE', bannedReason: undefined }
      })
      
      return { prevCustomer }
    },
    onSuccess: () => {
      toast.success('Customer unbanned')
    },
    onError: (_err, id, context) => {
      if (context?.prevCustomer) {
        queryClient.setQueryData(queryKeys.customer(id), context.prevCustomer)
      }
      toast.error('Failed to unban customer')
    },
    onSettled: (_data, _err, id) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.customer(id) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.customers() })
    },
  })

  React.useEffect(() => {
    const timer = setTimeout(() => {
      // Only a changed term resets to page 1 — on mount the term is unchanged,
      // so reloads and shared links keep their ?page=.
      navigate({
        to: '.',
        search: (prev) => ((prev.search ?? '') === searchInput ? prev : { ...prev, search: searchInput, page: 1 }),
        replace: true,
      })
    }, 500)
    return () => clearTimeout(timer)
  }, [searchInput, navigate])

  const customersQuery = useQuery({
    queryKey: queryKeys.customers({ page, limit, search: searchQuery, customerType, status }),
    queryFn: () =>
      customersApi.customers.list({
        page,
        limit,
        search: searchQuery || undefined,
        customerType: customerType || undefined,
        // "ARCHIVED" is a view, not a customer status.
        status: status && status !== 'ARCHIVED' ? status : undefined,
        archived: status === 'ARCHIVED' || undefined,
      }),
    enabled: !!shopId,
  })

  const selectedCustomerQuery = useQuery({
    queryKey: queryKeys.customer(selectedId!),
    queryFn: () => customersApi.customers.get(selectedId!),
    enabled: !!selectedId && status !== 'ARCHIVED', // archived: the panel reads the list row
    staleTime: 60_000,
  })

  const customers: Customer[] = React.useMemo(() => customersQuery.data?.data ?? [], [customersQuery.data])
  const totalItems = customersQuery.data?.meta?.total ?? customers.length
  const totalPages = customersQuery.data?.meta?.totalPages ?? 1
  const loading = customersQuery.isLoading
  const isArchivedView = status === 'ARCHIVED' // read-only panel + Restore
  const showStatusColumn = !status // "All Statuses": say which one each row is

  // Auto-open the side panel with the first customer on arrival (products/sales
  // behavior). Closing it suppresses the auto-open until the view changes.
  const pageSignature = `${shopId}:${page}:${limit}:${searchQuery}:${customerType ?? ''}:${status ?? ''}`
  // A selected customer off this page (just restored, or a deep link) keeps the panel while their own record loads.
  const currentPageHasSelection = !!selectedId && (customers.some((c) => c.id === selectedId) || (!isArchivedView && (!!selectedCustomerQuery.data || selectedCustomerQuery.isFetching)))
  const closeSelectedPanel = React.useCallback(() => {
    suppressedAutoOpenSignature = pageSignature
    setSelectedId(null)
  }, [pageSignature, setSelectedId])

  React.useEffect(() => {
    if (loading || customers.length === 0) return
    if (currentPageHasSelection) return
    if (!selectedId && suppressedAutoOpenSignature === pageSignature) return
    // replace: the auto-selection must not pollute browser history.
    void navigate({
      to: '/customers/$item',
      params: { item: customers[0].id },
      search: (prev) => ({ ...prev }),
      replace: true,
    })
  }, [currentPageHasSelection, customers, loading, pageSignature, selectedId, navigate])

  React.useEffect(() => {
    return () => {
      // /customers and /customers/$item are sibling routes, so swapping the
      // selection REMOUNTS this page — only reset the suppression when actually
      // leaving the customers section, or closing the panel would instantly
      // auto-reopen.
      if (!window.location.pathname.startsWith('/customers')) {
        suppressedAutoOpenSignature = null
      }
    }
  }, [])

  const selectedCustomer =
    selectedCustomerQuery.data ??
    customers.find((c) => c.id === selectedId) ??
    null

  function handleRefresh() {
    setIsRefreshing(true)
    void customersQuery.refetch().then(() => {
      setTimeout(() => setIsRefreshing(false), 600)
    })
  }

  function handleTypeFilter(value: string) {
    navigate({ to: '.', search: (prev) => ({ ...prev, customerType: value === 'ALL' ? '' : value, page: 1 }), replace: true })
  }

  function handleStatusFilter(value: string) {
    navigate({ to: '.', search: (prev) => ({ ...prev, status: value === 'ALL' ? '' : value, page: 1 }), replace: true })
  }

  // One set of customer actions for the row "…" and the side panel "⋮" (CustomerActionsMenu).
  const customerActions: CustomerActions = {
    onEdit: (c) => setEditingCustomer(c),
    onStartSession: (c) => startSession.mutate(c),
    onFlag: (c) => { setActionReason(''); setFlaggingCustomer(c) },
    onUnflag: (c) => unflagCustomer.mutate(c.id),
    onMerge: (c) => setMergingCustomer(c),
    onBan: (c) => { setActionReason(''); setBanningCustomer(c) },
    onUnban: (c) => unbanCustomer.mutate(c.id),
    onArchive: (c) => setDeletingCustomer(c),
    onErase: (c) => setErasingCustomer(c),
  }

  function handleRowClick(customer: Customer) {
    setSelectedId(customer.id === selectedId ? null : customer.id)
  }

  return (
    <div className="space-y-6">
      <PageBreadcrumb />
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Customers</h2>
          <p className="text-muted-foreground">
            {!loading && totalItems > 0
              ? `${totalItems.toLocaleString()} total customers`
              : 'Manage your customer directory.'}
          </p>
        </div>
        {(customers.length > 0 || loading) && (
          <Button
            onClick={() => setIsAddSheetOpen(true)}
            className="transition-all hover:scale-105 hover:shadow-md"
          >
            <Plus className="mr-0.5 h-4 w-4" /> Add Customer
          </Button>
        )}
      </div>

      {(customers.length > 0 || searchQuery || customerType || status || loading) && (
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[200px] max-w-sm group">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Search customers..."
              className="pl-9 pr-8"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            {searchInput && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSearchInput('')}
                className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-muted-foreground hover:bg-muted hover:text-foreground transition-all"
              >
                <X className="h-4 w-4" />
                <span className="sr-only">Clear search</span>
              </Button>
            )}
          </div>
          <Button
            variant="outline"
            size="icon"
            className="h-9 w-9 shrink-0"
            title="Refresh"
            disabled={isRefreshing}
            onClick={handleRefresh}
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </Button>
          <Select value={customerType || 'ALL'} onValueChange={handleTypeFilter}>
            <SelectTrigger className="w-36">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All Types</SelectItem>
              <SelectItem value="MEDICAL">Medical</SelectItem>
              <SelectItem value="RECREATIONAL">Recreational</SelectItem>
              <SelectItem value="BOTH">Both</SelectItem>
            </SelectContent>
          </Select>
          <Select value={status || 'ALL'} onValueChange={handleStatusFilter}>
            <SelectTrigger className="w-36">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All Statuses</SelectItem>
              <SelectItem value="ACTIVE">Active</SelectItem>
              <SelectItem value="FLAGGED">Flagged</SelectItem>
              <SelectItem value="BANNED">Banned</SelectItem>
              {canManageCustomers && <SelectItem value="ARCHIVED">Archived</SelectItem>}
            </SelectContent>
          </Select>
        </div>
      )}

      {!isArchivedView && <ArchivedMatchHint search={searchQuery} className="-mt-2" onShowArchived={canManageCustomers ? () => handleStatusFilter('ARCHIVED') : undefined} />}
      <div className="flex flex-col md:flex-row gap-6 items-start">
        <div
          className={`flex-1 min-w-0 transition-all duration-300 ${!loading && customers.length === 0 ? '' : 'border rounded-md squircle bg-card shadow-sm overflow-hidden'} ${selectedCustomer ? 'w-full md:w-[calc(100%-340px)] lg:w-[calc(100%-380px)]' : 'w-full'}`}
        >
          {!loading && customers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 gap-6 text-center">
              <div className="relative w-24 h-24 opacity-10 grayscale shrink-0">
                <img src="/misc/logo.png" alt="RootPoint Logo" className="w-full h-full object-contain" />
              </div>
              <div className="flex flex-col gap-4 items-center">
                <p className="font-medium text-muted-foreground text-lg">No customers found</p>
                <Button onClick={() => setIsAddSheetOpen(true)} className="w-fit">
                  <Plus className="mr-2 h-4 w-4" /> Add Customer
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex flex-col relative">
                {selectedIds.size > 0 && !isArchivedView && (
                  <div className="absolute top-0 left-0 right-0 z-20 bg-primary h-12 flex items-center justify-between px-4 animate-in fade-in slide-in-from-top-2 duration-200 shadow-lg">
                    <div className="flex items-center gap-3">
                      <span className="text-primary-foreground text-sm font-semibold whitespace-nowrap">
                        {selectedIds.size} customer{selectedIds.size > 1 ? 's' : ''} selected
                      </span>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-primary-foreground hover:bg-white/10 h-8 px-2"
                        onClick={() => setSelectedIds(new Set())}
                      >
                        <X className="h-4 w-4 mr-1" /> Clear
                      </Button>
                    </div>

                    <div className="flex items-center gap-2">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button 
                            variant="secondary" 
                            size="sm" 
                            className="bg-white text-primary hover:bg-white/90 h-8 font-medium shadow-sm transition-all hover:scale-105"
                            disabled={bulkAddingToGroup.isPending}
                          >
                            {bulkAddingToGroup.isPending ? (
                              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                            ) : (
                              <Plus className="h-3.5 w-3.5 mr-1.5" />
                            )}
                            Add to Group
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56 p-1">
                          <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                            Select Group
                          </div>
                          <Separator className="my-1" />
                          {groupsLoading ? (
                            <div className="p-4 flex items-center justify-center">
                              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                            </div>
                          ) : allGroups.length === 0 ? (
                            <div className="p-4 text-center text-xs text-muted-foreground">
                              No active groups found.
                            </div>
                          ) : (
                            <div className="max-h-60 overflow-y-auto">
                              {allGroups.map((g) => (
                                <DropdownMenuItem
                                  key={g.id}
                                  className="gap-2 py-2"
                                  onClick={() => bulkAddingToGroup.mutate(g.id)}
                                >
                                  {g.colorCode && (
                                    <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: g.colorCode }} />
                                  )}
                                  <span className="flex-1 truncate">{g.name}</span>
                                </DropdownMenuItem>
                              ))}
                            </div>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>

                      {canManageCustomers && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-primary-foreground hover:bg-white/10"
                          title="Archive selected"
                          disabled={bulkAddingToGroup.isPending}
                          onClick={() => setBulkDeletingIds(Array.from(selectedIds))}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                )}
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50 hover:bg-muted/50">
                      <TableHead className="w-[40px] pl-4">
                        <div className="flex items-center">
                          <Checkbox
                            checked={
                              customers.length > 0 && customers.every(c => selectedIds.has(c.id))
                                ? true
                                : selectedIds.size > 0
                                  ? 'indeterminate'
                                  : false
                            }
                            onCheckedChange={(checked) => {
                              if (checked === true) {
                                setSelectedIds(new Set([...selectedIds, ...customers.map(c => c.id)]))
                              } else {
                                const newIds = new Set(selectedIds)
                                customers.forEach(c => newIds.delete(c.id))
                                setSelectedIds(newIds)
                              }
                            }}
                          />
                        </div>
                      </TableHead>
                      <TableHead className="w-[220px]">Name</TableHead>
                      {showStatusColumn && <TableHead className={`hidden md:table-cell ${selectedCustomer ? 'md:hidden xl:table-cell' : ''}`}>Status</TableHead>}
                      <TableHead className={`hidden md:table-cell ${selectedCustomer ? 'md:hidden xl:table-cell' : ''}`}>Type</TableHead>
                      <TableHead className={`hidden md:table-cell ${selectedCustomer ? 'md:hidden xl:table-cell' : ''}`}>Groups</TableHead>
                      <TableHead className={`hidden lg:table-cell ${selectedCustomer ? 'lg:hidden xl:table-cell' : ''}`}>Loyalty Pts</TableHead>
                      <TableHead className="text-right w-[50px] pr-4"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading
                      ? Array.from({ length: 8 }).map((_, i) => (
                          <TableRow key={i}>
                            <TableCell className="pl-4"><Skeleton className="h-4 w-4 rounded-[4px]" /></TableCell>
                            <TableCell>
                              <div className="flex items-center gap-3">
                                <Skeleton className="h-8 w-8 rounded-full" />
                                <div className="space-y-1.5">
                                  <Skeleton className="h-4 w-[130px]" />
                                  <Skeleton className="h-3 w-[90px]" />
                                </div>
                              </div>
                            </TableCell>
                            {showStatusColumn && <TableCell className={`hidden md:table-cell ${selectedCustomer ? 'md:hidden xl:table-cell' : ''}`}><Skeleton className="h-5 w-14 rounded-full" /></TableCell>}
                            <TableCell className={`hidden md:table-cell ${selectedCustomer ? 'md:hidden xl:table-cell' : ''}`}>
                              <Skeleton className="h-5 w-16 rounded-full" />
                            </TableCell>
                            <TableCell className={`hidden md:table-cell ${selectedCustomer ? 'md:hidden xl:table-cell' : ''}`}>
                              <Skeleton className="h-5 w-20 rounded-full" />
                            </TableCell>
                            <TableCell className={`hidden lg:table-cell ${selectedCustomer ? 'lg:hidden xl:table-cell' : ''}`}>
                              <Skeleton className="h-4 w-12" />
                            </TableCell>
                            <TableCell className="text-right pr-4">
                              <Skeleton className="h-8 w-8 rounded-full ml-auto" />
                            </TableCell>
                          </TableRow>
                        ))
                      : customers.map((customer) => {
                          const groups = customer.groupMemberships?.map((m) => m.customerGroup) ?? []
                          const visibleGroups = groups.slice(0, 2)
                          const hiddenCount = groups.length - visibleGroups.length
                          const isPanelSelected = selectedId === customer.id
                          const isRowSelected = selectedIds.has(customer.id)
  
                          return (
                            <TableRow
                              key={customer.id}
                              className={`cursor-pointer transition-colors group ${isPanelSelected || isRowSelected ? 'bg-muted' : 'hover:bg-muted/50'}`}
                              onClick={() => handleRowClick(customer)}
                            >
                              <TableCell className="w-[40px] pl-4" onClick={(e) => e.stopPropagation()}>
                                <div className="flex items-center">
                                  <Checkbox
                                    checked={isRowSelected}
                                    onCheckedChange={() => handleRowSelect(customer.id)}
                                  />
                                </div>
                              </TableCell>
                              <TableCell className="font-medium max-w-[220px] overflow-hidden">
                                <div className="flex items-center gap-3">
                                  <AvatarCircle firstName={customer.firstName} lastName={customer.lastName} avatarUrl={customer.avatarUrl} />
                                  <div className="min-w-0 flex-1">
                                    <TooltipProvider>
                                      <div className="flex items-center">
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <span className="truncate cursor-help group-hover:text-primary transition-colors">
                                              {customer.firstName} {customer.lastName}
                                            </span>
                                          </TooltipTrigger>
                                          <TooltipContent align="start">
                                            <p className="text-sm font-medium">{customer.firstName} {customer.lastName}</p>
                                          </TooltipContent>
                                        </Tooltip>
                                        <CustomerStatusDot status={customer.status} />
                                      </div>
                                    </TooltipProvider>
                                    {customer.email && (
                                      <span className="block text-xs text-muted-foreground truncate">{customer.email}</span>
                                    )}
                                  </div>
                                </div>
                              </TableCell>
                              {showStatusColumn && <TableCell className={`hidden md:table-cell ${selectedCustomer ? 'md:hidden xl:table-cell' : ''}`}><CustomerStatusBadge status={customer.status} /></TableCell>}
                              <TableCell className={`hidden md:table-cell ${selectedCustomer ? 'md:hidden xl:table-cell' : ''}`}>
                                <CustomerTypeBadge type={customer.customerType} />
                              </TableCell>
                              <TableCell className={`hidden md:table-cell ${selectedCustomer ? 'md:hidden xl:table-cell' : ''}`}>
                                <div className="flex flex-wrap gap-1">
                                  {visibleGroups.map((g) => (
                                    <Badge key={g.id} variant="secondary" className="text-xs">
                                      {g.name}
                                    </Badge>
                                  ))}
                                  {hiddenCount > 0 && (
                                    <Badge variant="outline" className="text-xs text-muted-foreground">
                                      +{hiddenCount}
                                    </Badge>
                                  )}
                                  {groups.length === 0 && (
                                    <span className="text-muted-foreground text-xs">—</span>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className={`hidden lg:table-cell ${selectedCustomer ? 'lg:hidden xl:table-cell' : ''}`}>
                                <span className="text-sm">
                                  {customer.loyaltyProfile
                                    ? customer.loyaltyProfile.pointsBalance.toLocaleString()
                                    : '—'}
                                </span>
                              </TableCell>
                              <TableCell className="text-right pr-4">
                                {isArchivedView ? (
                                  <RestoreCustomerButton disabled={restoreCustomer.isPending} onRestore={() => restoreCustomer.mutate(customer.id)} />
                                ) : (
                                <CustomerActionsMenu
                                  customer={customer}
                                  actions={{ ...customerActions, onView: (c) => setSelectedId(c.id) }}
                                  trigger={
                                    <Button
                                      variant="ghost"
                                      className="h-8 w-8 p-0 opacity-100 xl:opacity-0 xl:group-hover:opacity-100 transition-opacity"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <span className="sr-only">Open menu</span>
                                      <MoreHorizontal className="h-4 w-4" />
                                    </Button>
                                  }
                                />
                                )}
                              </TableCell>
                            </TableRow>
                          )
                        })}
                  </TableBody>
                </Table>
              </div>
              {customers.length > 0 && (
                <DataTablePagination
                  page={page}
                  totalPages={totalPages}
                  totalItems={totalItems}
                  itemsPerPage={limit}
                  entityName="customers"
                  isLoading={loading}
                  onPageChange={(p: number) =>
                    navigate({ to: '.', search: (prev) => ({ ...prev, page: p }), replace: true })
                  }
                  onLimitChange={(l: number) =>
                    navigate({ to: '.', search: (prev) => ({ ...prev, limit: l, page: 1 }), replace: true })
                  }
                />
              )}
            </>
          )}
        </div>

        {selectedCustomer && isArchivedView && (
          <ArchivedCustomerPanel customer={selectedCustomer} onClose={closeSelectedPanel} onRestore={(c) => restoreCustomer.mutate(c.id)} isRestoring={restoreCustomer.isPending} onErase={isOwner ? (c) => setErasingCustomer(c) : undefined} />
        )}
        {selectedCustomer && !isArchivedView && (
          <React.Suspense fallback={null}>
            <CustomerSidePanel
              customer={selectedCustomer}
              shopId={shopId}
              onClose={closeSelectedPanel}
              onEdit={customerActions.onEdit}
              onDelete={customerActions.onArchive}
              onFlag={customerActions.onFlag}
              onUnflag={customerActions.onUnflag}
              onBan={customerActions.onBan}
              onUnban={customerActions.onUnban}
              onStartSession={customerActions.onStartSession}
              onMerge={customerActions.onMerge}
              onErase={customerActions.onErase}
            />
          </React.Suspense>
        )}
      </div>

      <React.Suspense fallback={null}>
        {isAddSheetOpen && (
          <AddCustomerSheet
            open={isAddSheetOpen}
            onOpenChange={setIsAddSheetOpen}
            shopId={shopId}
            onSuccess={(customer) => {
              setSelectedId(customer.id)
            }}
          />
        )}
        {erasingCustomer && (
          <EraseCustomerDialog customer={erasingCustomer} open onOpenChange={(o) => { if (!o) setErasingCustomer(null) }} onErased={() => setSelectedId(null)} />
        )}
        {mergingCustomer && (
          <MergeCustomerDialog customer={mergingCustomer} open onOpenChange={(o) => { if (!o) setMergingCustomer(null) }} onMerged={(c) => setSelectedId(c.id)} />
        )}
        {editingCustomer && (
          <AddCustomerSheet
            open={!!editingCustomer}
            onOpenChange={(open) => { if (!open) setEditingCustomer(null) }}
            shopId={shopId}
            customer={editingCustomer}
          />
        )}
      </React.Suspense>

      <ArchiveCustomerDialogs
        customer={deletingCustomer} onCancelSingle={() => setDeletingCustomer(null)} onConfirmSingle={(c) => deleteCustomer.mutate(c.id)} singlePending={deleteCustomer.isPending}
        bulkIds={bulkDeletingIds} onCancelBulk={() => setBulkDeletingIds(null)} onConfirmBulk={(ids) => bulkDeleteCustomers.mutate(ids)} bulkPending={bulkDeleteCustomers.isPending}
      />

      <Dialog open={!!flaggingCustomer} onOpenChange={(open) => { if (!open) { setFlaggingCustomer(null); setActionReason('') } }}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Flag customer</DialogTitle>
            <DialogDescription>
              {flaggingCustomer && <>Flag <strong>{flaggingCustomer.firstName} {flaggingCustomer.lastName}</strong>. A reason is required.</>}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="Reason for flagging"
            value={actionReason}
            onChange={(e) => setActionReason(e.target.value)}
            className="resize-none"
            rows={3}
          />
          <DialogFooter className="mt-2">
            <Button
              variant="ghost"
              disabled={flagCustomer.isPending}
              onClick={() => { setFlaggingCustomer(null); setActionReason('') }}
              className="text-muted-foreground border-none shadow-none hover:bg-transparent"
            >
              Cancel
            </Button>
            <Button
              disabled={flagCustomer.isPending || !actionReason.trim()}
              onClick={() => {
                if (flaggingCustomer && actionReason.trim()) {
                  flagCustomer.mutate({ id: flaggingCustomer.id, flagReason: actionReason.trim() })
                }
              }}
              className="bg-amber-500 hover:bg-amber-600 text-white min-w-[100px]"
            >
              {flagCustomer.isPending ? 'Flagging...' : 'Flag Customer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!banningCustomer} onOpenChange={(open) => { if (!open) { setBanningCustomer(null); setActionReason('') } }}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Ban customer</DialogTitle>
            <DialogDescription>
              {banningCustomer && <>Ban <strong>{banningCustomer.firstName} {banningCustomer.lastName}</strong>. A reason is required.</>}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="Reason for banning"
            value={actionReason}
            onChange={(e) => setActionReason(e.target.value)}
            className="resize-none"
            rows={3}
          />
          <DialogFooter className="mt-2">
            <Button
              variant="ghost"
              disabled={banCustomer.isPending}
              onClick={() => { setBanningCustomer(null); setActionReason('') }}
              className="text-muted-foreground border-none shadow-none hover:bg-transparent"
            >
              Cancel
            </Button>
            <Button
              disabled={banCustomer.isPending || !actionReason.trim()}
              onClick={() => {
                if (banningCustomer && actionReason.trim()) {
                  banCustomer.mutate({ id: banningCustomer.id, bannedReason: actionReason.trim() })
                }
              }}
              className="bg-red-600 hover:bg-red-700 text-white min-w-[100px]"
            >
              {banCustomer.isPending ? 'Banning...' : 'Ban Customer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
